import GrecoReactGrid, {
  standardCellTypeHandlers,
} from "components/grecoreactgrid/GrecoReactGrid";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppStoreState } from "store/AppStore";
import { useImportedInvoicesStore } from "store/ImportedInvoicesStore";
import { importedInvoiceColumnConfig } from "./importedInvoiceColumnConfig";
import CustomTextCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomTextCellTypeHandler";
import CustomNumberCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomNumberCellTypeHandler";
import CustomCheckmarkCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/CustomCheckmarkCellTypeHandler";
import SortableHeaderCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/SortableHeaderCellTypeHandler";
import SumHeaderCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/SumHeaderCellTypeHandler";
import OrdinalNumberCellTypeHandler from "components/grecoreactgrid/customcelltypehandlers/OrdinalNumberCellTypeHandler";
import SelectCellTypeHandler from "./customcelltypehandlers/SelectCellTypeHandler";
import SelectAllCellTypeHandler from "./customcelltypehandlers/SelectAllCellTypeHandler";
import ImportedInvoiceOptionsCellTypeHandler from "./customcelltypehandlers/ImportedInvoiceOptionsCellTypeHandler";
import { DeleteImportedInvoiceDialog } from "./DeleteImportedInvoiceDialog";
import { RematchImportedInvoicesDialog } from "./RematchImportedInvoicesDialog";
import { ImportImportedInvoicesDialog } from "./ImportImportedInvoicesDialog";
import { PullImportedInvoicesDialog } from "./PullImportedInvoicesDialog";
import { useTheme } from "@emotion/react";

const ImportedInvoiceGrid = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { taxonomy, grecoCompanies } = useAppStoreState();
  const importedInvoicesStore = useImportedInvoicesStore();
  const [
    {
      importedInvoices,
      importedInvoiceMetadataMap,
      searchTerm,
      fromDateFilter,
      toDateFilter,
      matchStateFilter,
      importedInvoicesSort,
      deleteImportedInvoiceDialog,
      rematchImportedInvoicesDialog,
      importImportedInvoicesDialog,
      pullImportedInvoicesDialog,
    },
    {
      loadImportedInvoices,
      filterImportedInvoices,
      setImportedInvoicesStoreValues,
      setImportedInvoicesStoreValue,
    },
  ] = importedInvoicesStore;

  useEffect(() => {
    if (grecoCompanies?.length > 0) {
      loadImportedInvoices({ t });
    }
  }, [
    fromDateFilter,
    toDateFilter,
    matchStateFilter,
    grecoCompanies,
  ]);

  useEffect(() => {
    if (importedInvoices) {
      filterImportedInvoices({ searchTerm });
    }
  }, [searchTerm, importedInvoices]);

  const handleChanges = (
    updatedRows: any[],
    updatedRowsMetadataMap: { [key: number]: any }
  ) => {
    setImportedInvoicesStoreValues({
      importedInvoices: importedInvoices.map((entry) => {
        const updatedEntry = updatedRows.find(
          (row) => row.importedInvoiceId === entry.importedInvoiceId
        );
        return updatedEntry ? updatedEntry : entry;
      }),
      importedInvoiceMetadataMap: updatedRowsMetadataMap,
    });
  };

  const cellTypeHandlers = {
    ...standardCellTypeHandlers,
    customtext: new CustomTextCellTypeHandler(),
    customnumber: new CustomNumberCellTypeHandler(),
    customcheckmark: new CustomCheckmarkCellTypeHandler(),
    select: new SelectCellTypeHandler(),
    selectall: new SelectAllCellTypeHandler(),
    sortableheader: new SortableHeaderCellTypeHandler(),
    sumheader: new SumHeaderCellTypeHandler(),
    ordinalnumber: new OrdinalNumberCellTypeHandler(),
    importedinvoiceoptions: new ImportedInvoiceOptionsCellTypeHandler(),
  };

  return (
    <>
      <div style={{ height: "100%", overflow: "auto" }}>
        <div
          data-help="imported-invoices-grid"
          style={{ height: "calc(100% - 10px)", overflow: "scroll" }}
        >
          <GrecoReactGrid
            rows={importedInvoices}
            columns={importedInvoiceColumnConfig}
            idColumn={"importedInvoiceId"}
            rowsMetadataMap={importedInvoiceMetadataMap}
            handleChanges={handleChanges}
            cellTypeHandlers={cellTypeHandlers}
            keyColumn="importedInvoiceId"
            hasSumHeaderRow={false}
            stickyColumns={{
              left: 3,
              right: 1,
              top: 1,
            }}
            context={{
              taxonomy: taxonomy,
              t: t,
              importedInvoicesStore: importedInvoicesStore,
              theme: theme,
              grecoCompanies: grecoCompanies,
              sort: importedInvoicesSort,
              setSort: (sort: any) => {
                setImportedInvoicesStoreValue("importedInvoicesSort", sort);
              },
            }}
            sort={importedInvoicesSort}
          />
        </div>
      </div>
      {deleteImportedInvoiceDialog.isOpen && <DeleteImportedInvoiceDialog />}
      {rematchImportedInvoicesDialog.isOpen && <RematchImportedInvoicesDialog />}
      {importImportedInvoicesDialog.isOpen && <ImportImportedInvoicesDialog />}
      {pullImportedInvoicesDialog.isOpen && <PullImportedInvoicesDialog />}
    </>
  );
};

export default ImportedInvoiceGrid;
